import {type Control, Controller, type FieldValues, type Path} from "react-hook-form";
import {FormFeedback, FormGroup, Input, Label} from "reactstrap";

type FloatingFieldProps<T extends FieldValues> = {
  name: Path<T>;
  idPrefix?: string;
  label: string;
  control: Control<T>;
  error?: {message?: string};
  disabled?: boolean;
  type?: string;
  required?: boolean;
  /**
   * Optional input-time normalizer. Applied to the raw input value before it
   * reaches the form state. Useful to silently strip noise like extra spaces
   * (e.g. for a zip code) so user typos don't fail strict validation.
   */
  normalize?: (value: string) => string;
  formatOnBlur?: (value: string) => string;
};

export function FloatingField<T extends FieldValues>({
  name,
  idPrefix = "",
  label,
  control,
  error,
  disabled,
  type,
  required,
  normalize,
  formatOnBlur,
}: FloatingFieldProps<T>) {
  return (
    <FormGroup floating>
      <Controller
        name={name}
        control={control}
        render={({field: {ref, ...field}}) => (
          <Input
            {...field}
            innerRef={ref}
            value={field.value ?? ""}
            onChange={
              normalize ? (e) => field.onChange(normalize(e.target.value)) : field.onChange
            }
            onBlur={
              formatOnBlur
                ? (event) => {
                    field.onChange(formatOnBlur(event.target.value));
                    field.onBlur();
                  }
                : field.onBlur
            }
            type={type as any}
            disabled={disabled}
            invalid={!!error}
            placeholder={label}
            id={`${idPrefix}field-${name}`}
          />
        )}
      />
      <Label for={`${idPrefix}field-${name}`}>
        {label}
        {required && " *"}
      </Label>
      {error && <FormFeedback>{error.message}</FormFeedback>}
    </FormGroup>
  );
}
